import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { b2bGetJobs, b2bCreateJob, b2bGetLists, b2bGetStreams } from '../../../lib/adminApi';
import AdminSidebar from '../../../components/AdminSidebar';

const NAVY = '#0a2540'; const ORANGE = '#F37428'; const BLUE = '#0176CE';

const STATUS_COLORS = {
  draft: { bg: 'rgba(100,116,139,0.08)', color: '#64748b' },
  active: { bg: 'rgba(5,150,105,0.08)', color: '#059669' },
  paused: { bg: 'rgba(217,119,6,0.08)', color: '#d97706' },
  completed: { bg: 'rgba(1,118,206,0.08)', color: '#0176CE' },
};

export default function B2BMarketingJobs() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [lists, setLists] = useState([]);
  const [streams, setStreams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ job_name: '', list_id: '', stream_id: '' });
  const [saving, setSaving] = useState(false);
  const [sortCol, setSortCol] = useState('created_on_utc');
  const [sortDir, setSortDir] = useState('desc');

  function load() {
    setLoading(true);
    b2bGetJobs().then(r => { setJobs(r.jobs || []); setLoading(false); }).catch(() => setLoading(false));
  }

  useEffect(() => {
    load();
    b2bGetLists().then(r => setLists(r.lists || [])).catch(() => {});
    b2bGetStreams().then(r => setStreams(r.streams || [])).catch(() => {});
  }, []);

  function toggleSort(col) {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  }

  const sorted = [...jobs].sort((a, b) => {
    const av = a[sortCol] ?? ''; const bv = b[sortCol] ?? '';
    return sortDir === 'asc' ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
  });

  async function handleCreate(e) {
    e.preventDefault(); setSaving(true);
    try {
      const r = await b2bCreateJob({ ...form, list_id: parseInt(form.list_id), stream_id: parseInt(form.stream_id) });
      navigate(`/admin/b2b/jobs/${r.job_id}`);
    } catch (err) { alert(err.message); setSaving(false); }
  }

  function ind(col) { if (sortCol !== col) return ' ↕'; return sortDir === 'asc' ? ' ↑' : ' ↓'; }

  return (
    <div style={s.page}>
      <AdminSidebar active="b2b-jobs" />
      <main style={s.main}>
        <div style={s.topRow}>
          <h1 style={s.title}>Marketing Jobs</h1>
          <button style={s.primaryBtn} onClick={() => setShowNew(true)}>+ New Job</button>
        </div>

        {showNew && (
          <div style={s.modal}>
            <div style={s.modalBox}>
              <h2 style={{ margin: '0 0 16px', color: NAVY, fontSize: 18 }}>New Marketing Job</h2>
              <form onSubmit={handleCreate} style={{ display: 'grid', gap: 12 }}>
                <input required style={s.inp} placeholder="Job Name *" value={form.job_name} onChange={e => setForm(f => ({ ...f, job_name: e.target.value }))} />
                <label style={s.label}>Contact List *
                  <select required style={s.inp} value={form.list_id} onChange={e => setForm(f => ({ ...f, list_id: e.target.value }))}>
                    <option value="">Select list…</option>
                    {lists.map(l => <option key={l.list_id} value={l.list_id}>{l.list_name} ({l.member_count} contacts)</option>)}
                  </select>
                </label>
                <label style={s.label}>Email Stream *
                  <select required style={s.inp} value={form.stream_id} onChange={e => setForm(f => ({ ...f, stream_id: e.target.value }))}>
                    <option value="">Select stream…</option>
                    {streams.map(s => <option key={s.stream_id} value={s.stream_id}>{s.stream_name} ({s.step_count} steps)</option>)}
                  </select>
                </label>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button type="button" style={s.cancelBtn} onClick={() => setShowNew(false)}>Cancel</button>
                  <button type="submit" style={s.primaryBtn} disabled={saving}>{saving ? 'Creating…' : 'Create Job'}</button>
                </div>
              </form>
            </div>
          </div>
        )}

        <div style={s.card}>
          <table style={s.table}>
            <thead>
              <tr>
                {[['job_name','Job'], ['list_name','List'], ['stream_name','Stream'], ['status','Status'], ['enrolled_count','Enrolled'], ['active_count','Active'], ['completed_count','Done'], ['created_on_utc','Created']].map(([col, label]) => (
                  <th key={col} style={s.th} onClick={() => toggleSort(col)}>{label}{ind(col)}</th>
                ))}
                <th style={s.th}></th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={9} style={s.empty}>Loading…</td></tr>}
              {!loading && sorted.length === 0 && <tr><td colSpan={9} style={s.empty}>No marketing jobs yet</td></tr>}
              {sorted.map(j => {
                const sc = STATUS_COLORS[j.status] || STATUS_COLORS.draft;
                return (
                  <tr key={j.job_id} style={s.tr}>
                    <td style={{ ...s.td, fontWeight: 600, color: NAVY }}>
                      <Link to={`/admin/b2b/jobs/${j.job_id}`} style={{ color: NAVY, textDecoration: 'none' }}>{j.job_name}</Link>
                    </td>
                    <td style={s.td}>{j.list_name}</td>
                    <td style={s.td}>{j.stream_name}</td>
                    <td style={s.td}><span style={{ background: sc.bg, color: sc.color, borderRadius: 4, fontSize: 10, fontWeight: 600, padding: '2px 6px', textTransform: 'uppercase' }}>{j.status}</span></td>
                    <td style={{ ...s.td, textAlign: 'center' }}>{j.enrolled_count || 0}</td>
                    <td style={{ ...s.td, textAlign: 'center', color: '#059669', fontWeight: j.active_count > 0 ? 600 : 400 }}>{j.active_count || 0}</td>
                    <td style={{ ...s.td, textAlign: 'center' }}>{j.completed_count || 0}</td>
                    <td style={{ ...s.td, color: '#94a3b8', fontSize: 12 }}>{new Date(j.created_on_utc).toLocaleDateString()}</td>
                    <td style={s.td}><Link to={`/admin/b2b/jobs/${j.job_id}`} style={{ color: BLUE, fontSize: 12, textDecoration: 'none' }}>View →</Link></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}

const s = {
  page: { display: 'flex', minHeight: '100vh', fontFamily: "'Inter', system-ui, sans-serif" },
  main: { flex: 1, background: '#f0f5fb', padding: '36px 40px', overflowY: 'auto' },
  topRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 },
  title: { color: NAVY, fontSize: 24, fontWeight: 700, margin: 0 },
  primaryBtn: { background: ORANGE, color: '#fff', border: 'none', borderRadius: 8, padding: '8px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  cancelBtn: { background: '#f1f5f9', color: '#64748b', border: 'none', borderRadius: 8, padding: '8px 18px', fontSize: 13, cursor: 'pointer' },
  card: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'auto', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' },
  table: { width: '100%', borderCollapse: 'collapse', minWidth: 800 },
  th: { color: '#94a3b8', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', padding: '10px 14px', textAlign: 'left', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none' },
  tr: { borderBottom: '1px solid #f1f5f9' },
  td: { color: '#374151', fontSize: 13, padding: '10px 14px', verticalAlign: 'middle' },
  empty: { color: '#94a3b8', fontSize: 13, padding: '32px', textAlign: 'center' },
  label: { color: '#374151', fontSize: 12, fontWeight: 500, display: 'flex', flexDirection: 'column', gap: 4 },
  inp: { padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, width: '100%', boxSizing: 'border-box', marginTop: 2 },
  modal: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 },
  modalBox: { background: '#fff', borderRadius: 12, padding: '28px 32px', width: 480, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' },
};
